/**
 * Renders a "home point" marker on the map for every registered dock station,
 * plus a dashed geofence ring at each dock's configured radius. Mounts as a
 * side-effecting component (returns null) — the parent just needs to include
 * it once and the layer reflects the current `dockStations` array reactively.
 *
 * Same isolation contract as the other dock-demo layers: all sources/layers
 * are namespaced `dock-demo-homes-*` / `dock-demo-geofences-*`, the lifecycle
 * is fully owned by this component, and unmount fully tears everything down.
 *
 * Style-reload recovery: re-installs on every `style.load` event so MapCanvas
 * style swaps (e.g. orthophoto signed-URL re-resolves) don't leave the home
 * markers stranded — same pattern as useDemoWaypointPlacement.
 */

import { useEffect } from 'react';
import type { Map as MapboxMap, FilterSpecification } from 'mapbox-gl';
import { useMapStore } from '../../stores';
import { useDockDemoStore } from '../../stores/dockDemoStore';
import type { DemoDockStation } from './types';

const SRC_HOMES = 'dock-demo-homes-src';
const SRC_GEOFENCES = 'dock-demo-geofences-src';
const SRC_GEOFENCE_VERTS = 'dock-demo-geofence-verts-src';
const LAYER_GEOFENCE_LINE = 'dock-demo-geofences-line-lyr';
const LAYER_GEOFENCE_VERTS = 'dock-demo-geofence-verts-lyr';
const LAYER_HOMES_HALO = 'dock-demo-homes-halo-lyr';
const LAYER_HOMES_CIRCLE = 'dock-demo-homes-circle-lyr';
const LAYER_HOMES_LETTER = 'dock-demo-homes-letter-lyr';
const LAYER_HOMES_LABEL = 'dock-demo-homes-label-lyr';

const ALL_LAYERS = [
  LAYER_GEOFENCE_LINE,
  LAYER_GEOFENCE_VERTS,
  LAYER_HOMES_HALO,
  LAYER_HOMES_CIRCLE,
  LAYER_HOMES_LETTER,
  LAYER_HOMES_LABEL,
] as const;

const ALL_SOURCES = [SRC_HOMES, SRC_GEOFENCES, SRC_GEOFENCE_VERTS] as const;

/**
 * True when at least one of our sources or layers is missing — used by the
 * styledata recovery path so we can reinstall whenever external code wipes
 * our state, instead of waiting for the next style.load. The home marker
 * MUST always be present once a dock has been located: it's the user's
 * primary anchor point and any momentary disappearance reads as a bug.
 */
function isMissing(map: import('mapbox-gl').Map): boolean {
  for (const id of ALL_SOURCES) if (!map.getSource(id)) return true;
  for (const id of ALL_LAYERS) if (!map.getLayer(id)) return true;
  return false;
}

const EARTH_RADIUS_M = 6371000;

/**
 * Approximate a circle of `radiusM` meters around (lng, lat) as a closed
 * polyline. Uses an equirectangular meters-to-degrees approximation, which
 * is accurate enough for radii under ~10 km at non-polar latitudes — the
 * UI clamps geofence radius to 8000 m, so we're well within tolerance.
 */
function geoCirclePolygon(lng: number, lat: number, radiusM: number, steps = 64): [number, number][] {
  const coords: [number, number][] = [];
  const cosLat = Math.cos(lat * Math.PI / 180);
  if (cosLat === 0) return coords;
  const dLngPerM = (1 / EARTH_RADIUS_M) * (180 / Math.PI) / cosLat;
  const dLatPerM = (1 / EARTH_RADIUS_M) * (180 / Math.PI);
  for (let i = 0; i <= steps; i++) {
    const angle = (i / steps) * 2 * Math.PI;
    const dxM = radiusM * Math.cos(angle);
    const dyM = radiusM * Math.sin(angle);
    coords.push([lng + dxM * dLngPerM, lat + dyM * dLatPerM]);
  }
  return coords;
}

function buildHomeFeatures(stations: DemoDockStation[]) {
  // Skip docks at [0,0] — that's the uninitialised state before the user has
  // picked a location. Showing them at null island would just be noise.
  const valid = stations.filter((s) => s.lng !== 0 || s.lat !== 0);
  return {
    type: 'FeatureCollection' as const,
    features: valid.map((s) => ({
      type: 'Feature' as const,
      properties: { id: s.id, name: s.name, model: s.model },
      geometry: { type: 'Point' as const, coordinates: [s.lng, s.lat] },
    })),
  };
}

function buildGeofenceFeatures(stations: DemoDockStation[]) {
  // Source data carries features for ALL docks tagged with `dockId`.
  // Per-dock visibility is enforced at the LAYER level via setFilter
  // (an `['in', ['get','dockId'], ['literal', visibleDockIds]]` expr).
  // Toggling geofenceVisible flips setFilter — much lighter than a full
  // setData rebuild, and the unaffected docks' features stay in place.
  const features: GeoJSON.Feature<GeoJSON.LineString>[] = [];
  for (const s of stations) {
    if (s.geofence.type === 'radius') {
      // Circular fences need a valid home anchor; skip un-located docks.
      if (s.lng === 0 && s.lat === 0) continue;
      if (s.geofence.radiusM <= 0) continue;
      features.push({
        type: 'Feature',
        properties: { id: s.id, dockId: s.id, name: s.name, kind: 'radius' },
        geometry: {
          type: 'LineString',
          coordinates: geoCirclePolygon(s.lng, s.lat, s.geofence.radiusM),
        },
      });
    } else {
      // Polygon: emit a closed LineString once we have at least 2 vertices.
      // Closing the ring with a 3rd+ vertex visually signals "this is a polygon".
      const coords = s.geofence.coordinates;
      if (coords.length < 2) continue;
      const ring: [number, number][] = coords.length >= 3
        ? [...coords, coords[0]]
        : [...coords];
      features.push({
        type: 'Feature',
        properties: { id: s.id, dockId: s.id, name: s.name, kind: 'polygon' },
        geometry: { type: 'LineString', coordinates: ring },
      });
    }
  }
  return { type: 'FeatureCollection' as const, features };
}

function buildGeofenceVertexFeatures(stations: DemoDockStation[]) {
  // Same setFilter-driven visibility model as buildGeofenceFeatures.
  // Vertex features carry `dockId`; the layer's filter expression selects
  // only those whose dock has geofenceVisible !== false.
  const features: GeoJSON.Feature<GeoJSON.Point>[] = [];
  for (const s of stations) {
    if (s.geofence.type !== 'polygon') continue;
    s.geofence.coordinates.forEach((c, idx) => {
      features.push({
        type: 'Feature',
        properties: { id: `${s.id}-v${idx}`, dockId: s.id, index: idx },
        geometry: { type: 'Point', coordinates: c },
      });
    });
  }
  return { type: 'FeatureCollection' as const, features };
}

function ensureSourcesAndLayers(map: MapboxMap): void {
  if (!map.getSource(SRC_HOMES)) {
    map.addSource(SRC_HOMES, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getSource(SRC_GEOFENCES)) {
    map.addSource(SRC_GEOFENCES, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getSource(SRC_GEOFENCE_VERTS)) {
    map.addSource(SRC_GEOFENCE_VERTS, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }

  if (!map.getLayer(LAYER_GEOFENCE_LINE)) {
    // Geofence ring/polygon outline. Bumped from 1.5 → 2.5 px width and
    // 0.65 → 0.95 opacity after user-reported invisibility at typical
    // satellite zoom — was rendering correctly, just imperceptible.
    map.addLayer({
      id: LAYER_GEOFENCE_LINE,
      type: 'line',
      source: SRC_GEOFENCES,
      paint: {
        'line-color': '#fbbf24',
        'line-width': 2.5,
        'line-dasharray': [3, 2],
        'line-opacity': 0.95,
      },
    });
  }
  if (!map.getLayer(LAYER_GEOFENCE_VERTS)) {
    // Polygon vertex dots — the user clicks on the map and sees where each
    // vertex landed. Bumped 5 → 8 px after invisibility report; the larger
    // stroke + dark halo also carries against bright orthos / pale sky.
    map.addLayer({
      id: LAYER_GEOFENCE_VERTS,
      type: 'circle',
      source: SRC_GEOFENCE_VERTS,
      paint: {
        'circle-radius': 8,
        'circle-color': '#fbbf24',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#0f172a',
        'circle-opacity': 1,
      },
    });
  }
  if (!map.getLayer(LAYER_HOMES_HALO)) {
    // Soft glow behind the home disk so it's visible against bright orthos.
    map.addLayer({
      id: LAYER_HOMES_HALO,
      type: 'circle',
      source: SRC_HOMES,
      paint: {
        'circle-radius': 22,
        'circle-color': '#fbbf24',
        'circle-opacity': 0.18,
        'circle-stroke-width': 0,
      },
    });
  }
  if (!map.getLayer(LAYER_HOMES_CIRCLE)) {
    map.addLayer({
      id: LAYER_HOMES_CIRCLE,
      type: 'circle',
      source: SRC_HOMES,
      paint: {
        'circle-radius': 14,
        'circle-color': '#f59e0b',
        'circle-stroke-width': 2.5,
        'circle-stroke-color': '#ffffff',
      },
    });
  }
  if (!map.getLayer(LAYER_HOMES_LETTER)) {
    // Black "H" inside the amber disk — canonical helipad / home-point look.
    // No halo: black-on-amber has plenty of contrast on its own and a halo
    // would muddy the geometry of a thin character.
    map.addLayer({
      id: LAYER_HOMES_LETTER,
      type: 'symbol',
      source: SRC_HOMES,
      layout: {
        'text-field': 'H',
        'text-size': 17,
        'text-letter-spacing': 0.04,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#0f172a',
      },
    });
  }
  if (!map.getLayer(LAYER_HOMES_LABEL)) {
    map.addLayer({
      id: LAYER_HOMES_LABEL,
      type: 'symbol',
      source: SRC_HOMES,
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 11,
        'text-offset': [0, 1.6],
        'text-anchor': 'top',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#fbbf24',
        'text-halo-color': 'rgba(15, 23, 42, 0.9)',
        'text-halo-width': 1.5,
      },
    });
  }
}

function pinLayersToTop(map: MapboxMap): void {
  for (const id of ALL_LAYERS) {
    try {
      if (map.getLayer(id)) map.moveLayer(id);
    } catch {
      // ignore — layer may not exist yet on first call before ensure
    }
  }
}

/**
 * Apply a per-dock visibility filter to the geofence + vertex layers.
 * Each feature carries a `dockId`; the layer renders only those whose
 * dockId is in `visibleDockIds`. Empty list → nothing matches → all
 * geofences hidden. Toggling geofenceVisible flips the filter — lighter
 * than rebuilding the source via setData and doesn't disturb Mapbox's
 * symbol placement engine.
 */
function applyGeofenceVisibilityFilter(map: MapboxMap, visibleDockIds: string[]): void {
  const filter: FilterSpecification = [
    'in',
    ['get', 'dockId'],
    ['literal', visibleDockIds],
  ];
  for (const id of [LAYER_GEOFENCE_LINE, LAYER_GEOFENCE_VERTS]) {
    try {
      if (map.getLayer(id)) map.setFilter(id, filter);
    } catch {
      // ignore — layer may not exist yet
    }
  }
}

function teardownSourcesAndLayers(map: MapboxMap): void {
  for (const id of [...ALL_LAYERS].reverse()) {
    try {
      if (map.getLayer(id)) map.removeLayer(id);
    } catch {
      // map may be in teardown; defensive — see project memory `839ce22`
    }
  }
  for (const id of [SRC_HOMES, SRC_GEOFENCES, SRC_GEOFENCE_VERTS]) {
    try {
      if (map.getSource(id)) map.removeSource(id);
    } catch {
      // ignore
    }
  }
}

export default function DockHomeLayer() {
  const map = useMapStore((s) => s.map);
  const isMapReady = useMapStore((s) => s.isMapReady);
  const stations = useDockDemoStore((s) => s.dockStations);

  // Install / teardown lifecycle. Deps deliberately exclude `stations` so a
  // location edit doesn't tear down + recreate every layer (which would
  // flicker on every keystroke). On `style.load` we re-install + reload data.
  useEffect(() => {
    if (!map || !isMapReady) return;

    const reinstall = () => {
      ensureSourcesAndLayers(map);
      const s = useDockDemoStore.getState();
      const homeSrc = map.getSource(SRC_HOMES);
      const fenceSrc = map.getSource(SRC_GEOFENCES);
      const vertSrc = map.getSource(SRC_GEOFENCE_VERTS);
      if (homeSrc && 'setData' in homeSrc) homeSrc.setData(buildHomeFeatures(s.dockStations));
      if (fenceSrc && 'setData' in fenceSrc) fenceSrc.setData(buildGeofenceFeatures(s.dockStations));
      if (vertSrc && 'setData' in vertSrc) vertSrc.setData(buildGeofenceVertexFeatures(s.dockStations));
      // Per-dock visibility filter — flipped on each render so toggling
      // geofenceVisible only re-runs setFilter, not setData.
      const visibleDockIds = s.dockStations
        .filter((d) => d.geofenceVisible !== false)
        .map((d) => d.id);
      applyGeofenceVisibilityFilter(map, visibleDockIds);
      pinLayersToTop(map);
    };

    // Self-healing repin: each styledata event checks whether our state
    // survived. If anything is missing, do a full reinstall.
    const repin = () => {
      if (isMissing(map)) {
        reinstall();
      } else {
        pinLayersToTop(map);
      }
    };

    if (map.isStyleLoaded()) reinstall();
    map.on('style.load', reinstall);
    map.on('styledata', repin);

    return () => {
      map.off('style.load', reinstall);
      map.off('styledata', repin);
      teardownSourcesAndLayers(map);
    };
  }, [map, isMapReady]);

  // Data update on every dockStations change. setData on existing sources
  // is safe even when isStyleLoaded() is transiently false; only
  // ensureSourcesAndLayers and pinLayersToTop need a loaded style. See
  // reference_mapbox_setdata_gating memory for the rationale (MapboxDraw
  // dev-mode crash can wedge isStyleLoaded permanently false, otherwise
  // delayed setActiveProject(siteId) → dockStations updates wouldn't
  // render until the user manually re-triggers a state change).
  useEffect(() => {
    if (!map || !isMapReady) return;
    const styleLoaded = map.isStyleLoaded();
    if (styleLoaded) {
      ensureSourcesAndLayers(map);
    }
    const homeSrc = map.getSource(SRC_HOMES);
    const fenceSrc = map.getSource(SRC_GEOFENCES);
    const vertSrc = map.getSource(SRC_GEOFENCE_VERTS);
    if (homeSrc && 'setData' in homeSrc) {
      try { homeSrc.setData(buildHomeFeatures(stations)); } catch { /* style mid-mutation */ }
    }
    if (fenceSrc && 'setData' in fenceSrc) {
      try { fenceSrc.setData(buildGeofenceFeatures(stations)); } catch { /* same */ }
    }
    if (vertSrc && 'setData' in vertSrc) {
      try { vertSrc.setData(buildGeofenceVertexFeatures(stations)); } catch { /* same */ }
    }
    const visibleDockIds = stations
      .filter((d) => d.geofenceVisible !== false)
      .map((d) => d.id);
    applyGeofenceVisibilityFilter(map, visibleDockIds);
    if (styleLoaded) pinLayersToTop(map);
  }, [map, isMapReady, stations]);

  return null;
}
